<script setup lang="ts">
/**
 * CargoQtyDialog — Modal to set cargo quantity when adding
 */
import { ref, watch } from 'vue';

const props = defineProps<{
  visible: boolean;
  typeName: string;
}>();

const emit = defineEmits<{
  confirm: [quantity: number];
  cancel: [];
}>();

const qty = ref(1);

watch(() => props.visible, (v) => {
  if (v) qty.value = 1;
});

function confirm(): void {
  emit('confirm', qty.value);
}

function handleKeydown(e: KeyboardEvent): void {
  if (e.key === 'Enter') confirm();
  if (e.key === 'Escape') emit('cancel');
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
    <div v-if="props.visible" class="modal-overlay" @click.self="emit('cancel')">
      <div class="modal-dialog" @keydown="handleKeydown">
        <div class="modal-header">Add Cargo</div>
        <div class="modal-body">
          <p class="modal-text">{{ props.typeName }}</p>
          <label class="qty-label">
            Quantity:
            <input
              v-model.number="qty"
              type="number"
              min="1"
              max="10000"
              class="qty-input"
              autofocus
            />
          </label>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="emit('cancel')">Cancel</button>
          <button class="btn btn-primary" @click="confirm">Add</button>
        </div>
      </div>
    </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-dialog {
  background: var(--bg-panel);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  min-width: 280px;
  max-width: 360px;
}

.modal-header {
  padding: 10px 14px;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
}

.modal-body {
  padding: 12px 14px;
}

.modal-text {
  font-size: 0.8rem;
  color: var(--text-primary);
  margin-bottom: 10px;
}

.qty-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
  color: var(--text-secondary);
}

.qty-input {
  width: 70px;
  padding: 4px 8px;
  font-size: 0.82rem;
  background: var(--bg-darker);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  text-align: center;
}

.qty-input:focus {
  outline: none;
  border-color: var(--accent-blue);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 14px;
  border-top: 1px solid var(--border-color);
}

.btn {
  padding: 5px 14px;
  font-size: 0.78rem;
  font-weight: 500;
  border-radius: var(--radius-sm);
  border: 1px solid transparent;
  cursor: pointer;
  transition: background var(--transition-fast);
}

.btn-secondary {
  background: var(--bg-hover);
  color: var(--text-primary);
  border-color: var(--border-color);
}

.btn-secondary:hover {
  background: var(--bg-secondary);
}

.btn-primary {
  background: var(--accent-blue);
  color: white;
}

.btn-primary:hover {
  background: #3a8eef;
}

/* Modal transitions */
.modal-enter-active { transition: opacity 0.2s ease; }
.modal-leave-active { transition: opacity 0.15s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
</style>
